import { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Link, LinearProgress, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useActivatePartnerAccountMutation } from '../../api/use-activate-partner-account-mutation';
import { persistSessionRole } from '../../services/auth';
import { USER_ROLES } from '../../constants';
import {
  getPasswordStrengthChecks,
  isStrongPassword,
  PARTNER_ACTIVATE_PASSWORD_REQUIREMENTS,
  validatePartnerActivatePassword,
} from '../PartnerSettings/PartnerSettingsSection/securityValidation';
import AuthPageShell from './AuthPageShell';
import AuthPasswordField from './AuthPasswordField';
import { authPrimaryButtonSx } from './AuthStyles';

const strengthLabels = ['Weak', 'Fair', 'Good', 'Strong', 'Excellent'];

const PartnerActivateAccount = () => {
  const navigate = useNavigate();
  const { partnerId } = useParams();
  const [searchParams] = useSearchParams();
  const resolvedPartnerId = String(partnerId ?? '').trim();
  const activationToken = String(searchParams.get('token') ?? '').trim();
  const hasActivationLink = Boolean(resolvedPartnerId && activationToken);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    persistSessionRole(USER_ROLES.PARTNER);
  }, []);

  const activateMutation = useActivatePartnerAccountMutation({
    onSuccess: (result) => {
      toast.success(result.message || 'Account activated. You can sign in now.');
      setPassword('');
      setConfirmPassword('');
      setErrors({});
      navigate('/partner-login', { replace: true });
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to activate account');
    },
  });

  const isSubmitting = activateMutation.isPending;
  const strength = getPasswordStrengthChecks(password);
  const passwordsMatch =
    Boolean(password) && Boolean(confirmPassword) && password === confirmPassword;
  const canActivate =
    hasActivationLink && isStrongPassword(password) && passwordsMatch && !isSubmitting;

  const requirementMet = [
    strength.minLength,
    strength.uppercase,
    strength.lowercase,
    strength.number,
    strength.special,
  ];
  const strengthScore = requirementMet.filter(Boolean).length;
  const strengthPercent = (strengthScore / requirementMet.length) * 100;
  const strengthLabel =
    password.length === 0 ? '' : strengthLabels[Math.max(0, strengthScore - 1)] || 'Weak';
  const strengthColor =
    strengthScore >= 5 ? 'success.main' : strengthScore >= 3 ? 'warning.main' : 'error.main';

  const clearFieldError = useCallback((field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleSetPassword = (event) => {
    event.preventDefault();

    if (!hasActivationLink) {
      toast.error('Partner activation link is invalid.');
      return;
    }

    const validationErrors = validatePartnerActivatePassword({
      password,
      confirmPassword,
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    activateMutation.mutate({ partnerId: resolvedPartnerId, password, token: activationToken });
  };

  return (
    <AuthPageShell
      maxWidth={460}
      title="Activate your account"
      subtitle="Create a strong password to finish setting up your partner portal access."
      footer={
        <Typography variant="body2" align="center" color="text.secondary">
          Already activated?{' '}
          <Link
            component={RouterLink}
            to="/partner-login"
            color="primary.main"
            underline="hover"
            fontWeight={600}
          >
            Sign in
          </Link>
        </Typography>
      }
    >
      {!hasActivationLink ? (
        <Typography variant="body2" color="error" align="center" sx={{ mb: 2 }}>
          This activation link is invalid. Use the link from your activation email, or ask the
          SureSpace team to send a new one.
        </Typography>
      ) : (
        <Box component="form" onSubmit={handleSetPassword} noValidate>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.65,
              mb: 2.25,
              borderRadius: '999px',
              bgcolor: (theme) => theme.portal?.accentSoft || 'action.hover',
              color: 'primary.main',
              border: '1px solid',
              borderColor: (theme) => `${theme.palette.primary.main}33`,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 15 }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.02em' }}>
              Secure account setup
            </Typography>
          </Box>

          <Box
            sx={{
              mb: 2.5,
              p: 1.75,
              borderRadius: '14px',
              bgcolor: (theme) => theme.portal?.surfaceMuted || 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: 'text.primary',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                mb: 1.25,
              }}
            >
              Password requirements
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1,
              }}
            >
              {PARTNER_ACTIVATE_PASSWORD_REQUIREMENTS.map((label, index) => {
                const met = requirementMet[index];
                return (
                  <Box
                    key={label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: met ? 'success.main' : 'transparent',
                        border: '1.5px solid',
                        borderColor: met ? 'success.main' : 'divider',
                        color: met ? 'common.white' : 'transparent',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <CheckRoundedIcon sx={{ fontSize: 12 }} />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.8125rem',
                        color: met ? 'text.primary' : 'text.secondary',
                        fontWeight: met ? 600 : 450,
                        lineHeight: 1.35,
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <AuthPasswordField
            id="partner-activate-password"
            label="New password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearFieldError('password');
            }}
            required
            disabled={isSubmitting}
            autoComplete="new-password"
            error={Boolean(errors.password)}
            helperText={errors.password}
          />

          {password ? (
            <Box sx={{ mt: 0.5, mb: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>
                  Password strength
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: strengthColor, fontWeight: 700 }}>
                  {strengthLabel}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={strengthPercent}
                sx={{
                  height: 6,
                  borderRadius: 999,
                  bgcolor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    bgcolor: strengthColor,
                  },
                }}
              />
            </Box>
          ) : null}

          <AuthPasswordField
            id="partner-activate-confirm-password"
            label="Confirm password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              clearFieldError('confirmPassword');
            }}
            required
            disabled={isSubmitting}
            autoComplete="new-password"
            error={Boolean(errors.confirmPassword)}
            helperText={errors.confirmPassword}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disableElevation
            disabled={!canActivate}
            sx={authPrimaryButtonSx}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={22} color="inherit" />
                Activating…
              </Box>
            ) : (
              'Activate account'
            )}
          </Button>
        </Box>
      )}
    </AuthPageShell>
  );
};

export default PartnerActivateAccount;
